/**
 * AgentTemplateCard - Card component for displaying agent templates
 * Used in the template gallery for agent creation.
 */
import React from 'react';
import {
  CheckCircle,
  Star,
  Building2,
  Bot,
  Briefcase,
  HeadphonesIcon,
  CalendarCheck,
  Receipt,
  Target,
  Package,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { BidiText } from '../../../shared/components/common';
import { AgentTemplate } from '../types';

// Template icon mapping based on icon name or category
const TEMPLATE_ICONS: Record<string, React.ReactNode> = {
  bot: <Bot className="h-5 w-5" />,
  briefcase: <Briefcase className="h-5 w-5" />,
  headset: <HeadphonesIcon className="h-5 w-5" />,
  calendar: <CalendarCheck className="h-5 w-5" />,
  receipt: <Receipt className="h-5 w-5" />,
  target: <Target className="h-5 w-5" />,
  package: <Package className="h-5 w-5" />,
};

function getTemplateIcon(icon: string): React.ReactNode {
  return TEMPLATE_ICONS[icon] || <Bot className="h-5 w-5" />;
}

// Keyboard/ARIA props so template cards are operable as a radio option:
// focusable, Enter/Space selects. Used by both card variants.
function radioCardProps(isSelected: boolean, onSelect: () => void) {
  return {
    role: 'radio' as const,
    'aria-checked': isSelected,
    tabIndex: 0,
    onClick: onSelect,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect();
      }
    },
  };
}

interface AgentTemplateCardProps {
  template: AgentTemplate;
  isSelected: boolean;
  onSelect: () => void;
}

export function AgentTemplateCard({
  template,
  isSelected,
  onSelect,
}: AgentTemplateCardProps) {
  const { t } = useTranslation('aiAgents');

  // Seeded system templates carry an i18n_key so their name/description are
  // localized; the DB English columns are the fallback.
  const i18nKey = template.template_config?.i18n_key;
  const displayName = i18nKey
    ? t(`templates.seeded.${i18nKey}.name`, { defaultValue: template.name })
    : template.name;
  const displayDescription = i18nKey
    ? t(`templates.seeded.${i18nKey}.description`, {
        defaultValue: template.description ?? '',
      })
    : template.description;

  const sourceLabel = template.is_system
    ? t('templates.systemTemplate')
    : t('templates.companyTemplate');

  return (
    <Card
      className={cn(
        'h-full flex flex-col transition-all cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isSelected
          ? 'ring-2 ring-primary shadow-lg'
          : 'hover:shadow-md hover:border-muted-foreground/20'
      )}
      {...radioCardProps(isSelected, onSelect)}
    >
      <CardContent className="p-4 flex-1">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="flex text-primary">
              {getTemplateIcon(template.icon)}
            </span>
            <BidiText className="font-semibold">{displayName}</BidiText>
          </div>
          {isSelected && <CheckCircle className="h-4 w-4 text-primary shrink-0" />}
        </div>

        <p className="text-sm text-muted-foreground mb-3 line-clamp-2 min-h-[40px]">
          {displayDescription || t('templates.noDescription')}
        </p>

        <div className="flex items-center gap-2 flex-wrap">
          {template.category && (
            <Badge variant="outline">
              {t(`templates.categories.${template.category.toLowerCase()}`, {
                defaultValue: template.category,
              })}
            </Badge>
          )}
          <Badge
            variant="outline"
            className={cn(
              'text-xs',
              template.is_system && 'border-warning text-warning'
            )}
          >
            {template.is_system ? (
              <Star className="h-3 w-3 me-1" />
            ) : (
              <Building2 className="h-3 w-3 me-1" />
            )}
            {sourceLabel}
          </Badge>
        </div>

        {template.usage_count > 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            {t('templates.usedTimes', { count: template.usage_count })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Blank template "Start from Scratch" card
interface BlankTemplateCardProps {
  isSelected: boolean;
  onSelect: () => void;
}

export function BlankTemplateCard({ isSelected, onSelect }: BlankTemplateCardProps) {
  const { t } = useTranslation('aiAgents');

  return (
    <Card
      className={cn(
        'h-full flex flex-col transition-all cursor-pointer bg-primary/5',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isSelected
          ? 'ring-2 ring-primary shadow-lg'
          : 'hover:shadow-md hover:border-muted-foreground/20'
      )}
      {...radioCardProps(isSelected, onSelect)}
    >
      <CardContent className="p-4 flex-1">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            <span className="font-semibold">{t('templates.blankTemplate')}</span>
          </div>
          {isSelected && <CheckCircle className="h-4 w-4 text-primary" />}
        </div>

        <p className="text-sm text-muted-foreground mb-3 min-h-[40px]">
          {t('templates.blankTemplateDesc')}
        </p>

        <Badge>{t('templates.recommended')}</Badge>
      </CardContent>
    </Card>
  );
}
